import React, { useState, useEffect } from 'react';
import type { Meal, IngredientCatalog } from '../models/diary';
import { useIsMobile } from '../hooks/useIsMobile';
import { ingredientService } from '../services/ingredientService';
import { mealService, MealIngredientInput } from '../services/mealService';
import SearchableSelect from './SearchableSelect';
import IngredientFormModal from './IngredientFormModal';

interface MealEditorModalProps {
  open: boolean;
  patientId: string | number;
  meal?: Meal | null; // present → edit mode
  onSaved: (meal: Meal) => void;
  onCancel: () => void;
}

interface Row {
  ingredient_id: number | '';
  quantity: string; // grams, kept as string for the input
}

const round = (n: number) => Math.round(n * 10) / 10;

const MealEditorModal: React.FC<MealEditorModalProps> = ({ open, patientId, meal, onSaved, onCancel }) => {
  const isMobile = useIsMobile();
  const isEdit = !!meal;

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<Row[]>([{ ingredient_id: '', quantity: '' }]);
  const [catalog, setCatalog] = useState<IngredientCatalog[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [showIngredientModal, setShowIngredientModal] = useState(false);
  const [pendingRowIndex, setPendingRowIndex] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load the global ingredient library for the selects.
  const fetchCatalog = async () => {
    setLoadingCatalog(true);
    try {
      const response = await ingredientService.getAll(1, 500);
      setCatalog(response.results);
    } catch (e) {
      console.error('Error fetching ingredient catalog:', e);
    } finally {
      setLoadingCatalog(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCatalog();
      if (meal) {
        setName(meal.name || '');
        setNotes(meal.notes || '');
        setRows(
          (meal.ingredients || []).map(ing => ({
            ingredient_id: ing.ingredient_id ?? '',
            quantity: ing.quantity != null ? String(ing.quantity) : '',
          }))
        );
        if (!meal.ingredients || meal.ingredients.length === 0) {
          setRows([{ ingredient_id: '', quantity: '' }]);
        }
        // A meal reached through a diary item may arrive without its notes, so
        // pull the authoritative value from the meal's own endpoint.
        if (patientId && meal.id) {
          mealService.getOne(patientId, meal.id)
            .then(fresh => setNotes(fresh.notes || ''))
            .catch(() => {});
        }
      } else {
        setName('');
        setNotes('');
        setRows([{ ingredient_id: '', quantity: '' }]);
      }
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, meal]);

  const updateRow = (index: number, patch: Partial<Row>) => {
    setRows(prev => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addRow = () => setRows(prev => [...prev, { ingredient_id: '', quantity: '' }]);
  const removeRow = (index: number) => setRows(prev => prev.filter((_, i) => i !== index));

  const handleIngredientCreated = async (created: IngredientCatalog) => {
    setCatalog(prev => [created, ...prev]);
    if (pendingRowIndex !== null) {
      updateRow(pendingRowIndex, { ingredient_id: created.id });
    }
    setShowIngredientModal(false);
    setPendingRowIndex(null);
  };

  // Live macro totals across the current (valid) rows.
  const liveTotals = rows.reduce(
    (acc, row) => {
      const ing = catalog.find(c => c.id === row.ingredient_id);
      const qty = Number(row.quantity);
      if (!ing || !qty || qty <= 0) return acc;
      const f = qty / 100;
      acc.calories += (ing.calories || 0) * f;
      acc.protein += (ing.proteins || 0) * f;
      acc.carbs += (ing.carbohydrates || 0) * f;
      acc.fat += (ing.fats || 0) * f;
      acc.fiber += (ing.fiber || 0) * f;
      acc.water += (ing.water || 0) * f;
      return acc;
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, water: 0 }
  );

  const validRows = (): MealIngredientInput[] =>
    rows
      .filter(r => r.ingredient_id !== '' && Number(r.quantity) > 0)
      .map(r => ({ ingredient_id: Number(r.ingredient_id), quantity: Number(r.quantity) }));

  const handleSave = async () => {
    setError('');
    if (!name.trim()) {
      setError('El nombre de la comida es requerido.');
      return;
    }
    const ingredients = validRows();
    if (ingredients.length === 0) {
      setError('Añade al menos un ingrediente con su cantidad en gramos.');
      return;
    }
    setIsSubmitting(true);
    try {
      const saved = isEdit && meal
        ? await mealService.update(patientId, meal.id, { name: name.trim(), notes: notes.trim(), ingredients })
        : await mealService.create(patientId, { name: name.trim(), notes: notes.trim(), ingredients });
      onSaved(saved);
    } catch (e: any) {
      setError(e.response?.error || e.message || 'Error al guardar la comida.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  const labelStyle: React.CSSProperties = { fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '11px 14px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 15, outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1050, padding: isMobile ? '12px' : '20px',
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: isMobile ? '24px 20px' : '32px 36px',
        width: '100%', maxWidth: 640, maxHeight: '92vh', overflow: 'auto',
        boxShadow: '0 4px 32px rgba(0,0,0,0.18)',
      }}>
        <h2 style={{ marginTop: 0, marginBottom: 20, fontSize: 22, fontWeight: 600, color: '#1f2937' }}>
          {isEdit ? 'Editar Comida' : 'Nueva Comida'}
        </h2>

        {error && (
          <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: 8, marginBottom: 18, fontSize: 14 }}>
            {error}
          </div>
        )}

        <label style={{ display: 'block', marginBottom: 16 }}>
          <span style={labelStyle}>Nombre <span style={{ color: '#dc2626' }}>*</span></span>
          <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} placeholder="Ej: Pollo con arroz" />
        </label>

        <label style={{ display: 'block', marginBottom: 20 }}>
          <span style={labelStyle}>Notas</span>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            placeholder="Notas opcionales para esta comida"
          />
        </label>

        {/* Ingredient builder */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={labelStyle as React.CSSProperties}>Ingredientes</span>
            <button
              type="button"
              onClick={() => { setPendingRowIndex(null); setShowIngredientModal(true); }}
              style={{ background: 'none', border: 'none', color: '#38b2ac', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              + Crear ingrediente
            </button>
          </div>

          {loadingCatalog ? (
            <div style={{ padding: 12, fontSize: 13, color: '#6b7280', textAlign: 'center', background: '#f9fafb', borderRadius: 6 }}>
              Cargando ingredientes...
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rows.map((row, index) => (
                <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <SearchableSelect
                      options={catalog.map(c => ({ value: c.id, label: `${c.name} (${c.calories} kcal/100g)`, searchText: c.name }))}
                      value={row.ingredient_id}
                      onChange={(value) => updateRow(index, { ingredient_id: Number(value) })}
                      placeholder="-- Selecciona ingrediente --"
                      hasError={row.ingredient_id === '' && !!row.quantity}
                    />
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={row.quantity}
                    onChange={e => updateRow(index, { quantity: e.target.value })}
                    placeholder="g"
                    style={{ width: 80, padding: '10px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                  />
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    disabled={rows.length === 1}
                    style={{
                      background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6,
                      padding: '10px 12px', fontSize: 14, cursor: rows.length === 1 ? 'not-allowed' : 'pointer',
                      opacity: rows.length === 1 ? 0.5 : 1, lineHeight: 1,
                    }}
                    title="Quitar ingrediente"
                  >
                    🗑️
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addRow}
                style={{ alignSelf: 'flex-start', background: '#f3f4f6', color: '#374151', border: '1px dashed #cbd5e1', borderRadius: 6, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 2 }}
              >
                + Añadir ingrediente
              </button>
            </div>
          )}
        </div>

        {/* Live macro totals */}
        <div style={{ background: '#f0f9ff', border: '1px solid #e0f2fe', borderRadius: 8, padding: 12, marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0369a1', marginBottom: 6 }}>Totales de la comida</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, fontSize: 12, color: '#0c4a6e' }}>
            <div><strong>Cal:</strong> {round(liveTotals.calories)}</div>
            <div><strong>Prot:</strong> {round(liveTotals.protein)}g</div>
            <div><strong>Carb:</strong> {round(liveTotals.carbs)}g</div>
            <div><strong>Gras:</strong> {round(liveTotals.fat)}g</div>
            <div><strong>Fibr:</strong> {round(liveTotals.fiber)}g</div>
            <div><strong>Agua:</strong> {round(liveTotals.water)}ml</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '11px 24px', fontWeight: 600, fontSize: 15, cursor: isSubmitting ? 'not-allowed' : 'pointer', color: '#374151' }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            style={{ background: isSubmitting ? '#81c9c4' : '#38b2ac', color: '#fff', border: 'none', borderRadius: 6, padding: '11px 24px', fontWeight: 600, fontSize: 15, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
          >
            {isSubmitting ? 'Guardando...' : isEdit ? 'Guardar Comida' : 'Crear Comida'}
          </button>
        </div>
      </div>

      {/* Inline ingredient quick-create */}
      <IngredientFormModal
        open={showIngredientModal}
        onSave={async (data) => {
          const created = await ingredientService.create(data as any);
          await handleIngredientCreated(created);
        }}
        onCancel={() => { setShowIngredientModal(false); setPendingRowIndex(null); }}
      />
    </div>
  );
};

export default MealEditorModal;
